import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { LinkTo } from '@ember/routing';
import { t } from 'ember-intl';
import { PhChartPieSlice, PhClockClockwise, PhClockCounterClockwise } from 'ember-phosphor-icons';
import pageTitle from 'ember-page-title/helpers/page-title';
import PageHeader from 'tio-ui/components/page-header';
import SearchInput from 'tio-ui/components/search-input';
import PartnerReportsPage from 'tio-reporting/components/partner-reports-page';
import PartnerConsolidatedReportsPage from 'tio-reporting/components/partner/reports-page';
import { Section, VStack } from 'tio-ui/components/layout';
import { SCHEDULED_EXPORTS_FEATURE_FLAG } from 'tio-employee/types/scheduled-exports';
import { PARTNER_CONSOLIDATED_REPORTS_FLAG } from 'tio-employee/types/partner-reports';
import type FeaturesService from 'tio-common/services/features';
import type SessionContextService from 'tio-employee/services/session-context';
import { reportingCompanyId } from 'tio-employee/utils/reporting-company';

const SEARCH_DEBOUNCE_MS = 150;

interface S {
  Args: {
    model: {
      id: string;
    };
  };
}

class ReportingIndexRoute extends Component<S> {
  @service declare sessionContext: SessionContextService;
  @service declare features: FeaturesService;

  /** Lives here because the search input belongs in PageHeader's :toolbar. */
  @tracked searchQuery = '';

  setSearchQuery = (value: string) => {
    this.searchQuery = value;
  };

  clearSearch = () => {
    this.searchQuery = '';
  };

  get companyId() {
    const { rolesForCurrentCompany, currentCompany } = this.sessionContext;
    return reportingCompanyId(rolesForCurrentCompany, currentCompany?.id ?? undefined);
  }

  get showScheduledExports() {
    return this.features.isEnabled(SCHEDULED_EXPORTS_FEATURE_FLAG);
  }

  get isConsolidated() {
    return this.features.isEnabled(PARTNER_CONSOLIDATED_REPORTS_FLAG);
  }

  <template>
    {{pageTitle (t "reporting.reports")}}
    {{#if this.isConsolidated}}
      <VStack>
        <PageHeader
          @title={{t "reporting.reports"}}
          @subtitle={{t "reporting.page_subtitle"}}
          @icon={{PhChartPieSlice}}
          @sticky={{false}}
        >
          <:toolbar>
            {{#if this.companyId}}
              <SearchInput
                class="max-w-lg"
                @value={{this.searchQuery}}
                @onChange={{this.setSearchQuery}}
                @onClear={{this.clearSearch}}
                @debounce={{SEARCH_DEBOUNCE_MS}}
                @placeholder={{t "reporting.search_placeholder"}}
                data-test-partner-reports-search
              />
            {{/if}}
          </:toolbar>
          <:actions>
            {{#if this.showScheduledExports}}
              <LinkTo
                @route="authenticated.admin.reporting.exports"
                class="tio-inline-row-extra-tight tio-text-xs-semibold whitespace-nowrap rounded-lg border tio-border-strong tio-bg-surface px-3 py-2 tio-fg-primary hover:tio-bg-sunken"
                data-test-scheduled-exports-link
              >
                <PhClockClockwise class="h-4 w-4" />
                {{t "reporting.scheduled_runs"}}
              </LinkTo>
            {{/if}}
          </:actions>
        </PageHeader>
        {{#if this.companyId}}
          {{! Not a Section: the admin shell would draw it as a panel around sections that are already cards. }}
          <div class="w-full max-w-7xl">
            <PartnerConsolidatedReportsPage
              @companyId={{this.companyId}}
              @viewerRoute="authenticated.admin.reporting.report-viewer"
              @dashboardViewerRoute="authenticated.admin.reporting.dashboard-viewer"
              @searchQuery={{this.searchQuery}}
            />
          </div>
        {{/if}}
      </VStack>
    {{else}}
      {{! The pre-redesign page, so turning the flag off is a full rollback. }}
      <VStack>
        <PageHeader @a11yTitle={{t "reporting.reports"}} @sticky={{false}} />
        {{#if this.companyId}}
          <Section>
            <:header>{{t "reporting.reports"}}</:header>
            <:body>
              {{#if this.showScheduledExports}}
                <LinkTo
                  @route="authenticated.admin.reporting.exports"
                  class="tio-inline-row-extra-tight w-fit tio-text-sm-medium tio-fg-info-h hover:tio-fg-info"
                  data-test-scheduled-exports-link
                >
                  <PhClockCounterClockwise class="h-4 w-4" />
                  {{t "reporting.scheduled_exports"}}
                </LinkTo>
              {{/if}}
              <PartnerReportsPage
                @companyId={{this.companyId}}
                @viewerRoute="authenticated.admin.reporting.report-viewer"
                @reportTypes={{array "tas"}}
              />
            </:body>
          </Section>
        {{/if}}
      </VStack>
    {{/if}}
  </template>
}

export default ReportingIndexRoute;
